/**
 * Available Batch Card Component
 * Displays a single available batch with stock status and quick actions
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-005
 */

import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { AvailableBatchResponse } from '@/types/availableBatches';
import { ProductTypeConfig, formatQuantity } from '@/types/inventory';
import { QualityBadge } from './QualityBadge';
import { StockStatusBadge } from './StockStatusBadge';
import {
  Calendar,
  MapPin,
  Eye,
  ShoppingCart,
  TrendingUp,
  Clock,
  DollarSign,
  Shield
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface AvailableBatchCardProps {
  batch: AvailableBatchResponse;
  onViewDetails?: () => void;
  onAllocateToOrder?: () => void;
  className?: string;
}

export function AvailableBatchCard({
  batch,
  onViewDetails,
  onAllocateToOrder,
  className
}: AvailableBatchCardProps) {
  // Get product config with fallback for undefined or missing types
  const productConfig = ProductTypeConfig[batch.productType] || {
    icon: '📦',
    label: batch.productType || 'Unknown Product',
    abbreviation: 'UNK',
    bgColor: 'bg-gray-100',
    borderColor: 'border-gray-300'
  };

  const availablePercent = (batch.availableQuantity / batch.initialQuantity) * 100;
  const isLowStock = batch.stockLevel === 'LOW' || batch.stockLevel === 'CRITICAL';
  const isExpiringSoon = batch.daysRemaining <= 30;

  return (
    <Card
      className={cn(
        'hover:shadow-lg transition-all duration-200',
        isLowStock && 'border-l-4 border-l-orange-500',
        batch.stockLevel === 'CRITICAL' && 'border-l-red-500',
        className
      )}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-gray-900 truncate">
              {batch.batchNumber}
            </h3>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-2xl">{productConfig.icon}</span>
              <div>
                <div className="text-sm font-medium text-gray-900">
                  {productConfig.label}
                </div>
                <div className="text-xs text-gray-500">
                  {productConfig.abbreviation}
                </div>
              </div>
            </div>
          </div>
          {batch.pesoCompliant && (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
              <Shield className="mr-1 h-3 w-3" />
              PESO
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Stock Status - Prominent Display */}
        <div className="p-3 bg-gray-50 rounded-lg space-y-2">
          <StockStatusBadge
            level={batch.stockLevel}
            availablePercent={availablePercent}
            showIcon={true}
            showPercentage={true}
            size="md"
          />
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div>
              <div className="text-xs text-gray-500 mb-0.5">Available</div>
              <div className="text-lg font-bold text-gray-900">
                {formatQuantity(batch.availableQuantity, batch.quantityUnit)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-0.5">Total</div>
              <div className="text-lg font-semibold text-gray-600">
                {formatQuantity(batch.initialQuantity, batch.quantityUnit)}
              </div>
            </div>
          </div>
        </div>

        {/* Quality Grade */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Quality</span>
          <QualityBadge
            grade={batch.qualityGrade}
            purity={batch.purityPercentage}
            showPurity={true}
            size="md"
          />
        </div>

        {/* Price Per Unit */}
        <div className="flex items-center justify-between p-2 bg-blue-50 rounded">
          <div className="flex items-center gap-2 text-blue-900">
            <DollarSign className="h-4 w-4" />
            <span className="text-sm font-medium">Price per Unit</span>
          </div>
          <span className="text-lg font-bold text-blue-900">
            ₹{batch.pricePerUnit.toFixed(2)}
          </span>
        </div>

        {/* Expiry Information */}
        <div className={cn(
          'space-y-1 p-2 rounded',
          isExpiringSoon ? 'bg-orange-50' : 'bg-gray-50'
        )}>
          <div className="flex items-center gap-2 text-sm">
            <Calendar className={cn(
              'h-4 w-4',
              isExpiringSoon ? 'text-orange-600' : 'text-gray-600'
            )} />
            <span className={cn(
              'font-medium',
              isExpiringSoon ? 'text-orange-900' : 'text-gray-900'
            )}>
              Expires: {format(new Date(batch.expiryDate), 'MMM dd, yyyy')}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600 ml-6">
            <Clock className="h-3 w-3" />
            <span className={cn(
              isExpiringSoon ? 'text-orange-700 font-medium' : 'text-gray-600'
            )}>
              {batch.daysRemaining} days remaining
              {isExpiringSoon && ' ⚠️'}
            </span>
          </div>
        </div>

        {/* Storage Location */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <MapPin className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">{batch.storageLocation}</span>
        </div>

        {/* Estimated Delivery */}
        {batch.estimatedDeliveryDays !== undefined && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <TrendingUp className="h-4 w-4 flex-shrink-0" />
            <span>Est. delivery: {batch.estimatedDeliveryDays} day{batch.estimatedDeliveryDays !== 1 ? 's' : ''}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-2 pt-2">
          {onViewDetails && (
            <Button
              variant="outline"
              onClick={onViewDetails}
              className="w-full"
            >
              <Eye className="mr-2 h-4 w-4" />
              Details
            </Button>
          )}
          {onAllocateToOrder && (
            <Button
              onClick={onAllocateToOrder}
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={batch.availableQuantity === 0}
            >
              <ShoppingCart className="mr-2 h-4 w-4" />
              Allocate
            </Button>
          )}
        </div>

        {/* Low Stock Warning */}
        {isLowStock && (
          <div className={cn(
            'p-2 rounded-md text-xs font-medium',
            batch.stockLevel === 'CRITICAL'
              ? 'bg-red-50 text-red-800 border border-red-200'
              : 'bg-orange-50 text-orange-800 border border-orange-200'
          )}>
            {batch.stockLevel === 'CRITICAL'
              ? '⚠️ Critical stock level - Consider reorder'
              : '⚠️ Low stock alert - Monitor availability'}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
